import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SetURLSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, type ToolResult } from "@/lib/api";

interface UseCanvasToolsOptions {
	projectId: string | undefined;
	toolResults: ToolResult[];
	isLoadingTools: boolean;
	searchParams: URLSearchParams;
	setSearchParams: SetURLSearchParams;
}

/**
 * Studio tool launching on the Canvas: tool picker, Content Plan platform
 * modal state, and the `?tool=` / `?platform=` URL auto-trigger.
 */
export function useCanvasTools({
	projectId,
	toolResults,
	isLoadingTools,
	searchParams,
	setSearchParams,
}: UseCanvasToolsOptions) {
	const { i18n } = useTranslation();
	const queryClient = useQueryClient();
	const toolFromUrl = searchParams.get("tool");
	const platformFromUrl = searchParams.get("platform");

	// --- Tool Picker State ---
	const [isToolPickerOpen, setIsToolPickerOpen] = useState(false);
	const [loadingTool, setLoadingTool] = useState<string | null>(null);

	// --- Content Plan Platform Modal State ---
	const [isPlatformModalOpen, setIsPlatformModalOpen] = useState(false);
	const [pendingToolSlug, setPendingToolSlug] = useState<string | null>(null);
	const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);

	// --- Auto-trigger tool from URL ---
	useEffect(() => {
		if (projectId && toolFromUrl && toolResults && !isLoadingTools) {
			// Check if we already have a result for this tool
			// (This is a bit heuristic since tool results don't clearly state the slug,
			// but usually the label or content might match.
			// For simplicity, we'll let it run if the user came here with the intent.)

			// If it's content-plan, we need a platform
			if (toolFromUrl === "content-plan" && !platformFromUrl) {
				// We need to show the platform modal
				setIsPlatformModalOpen(true);
				setPendingToolSlug("content-plan");
				// Consume the param, or every toolResults refetch (and a page
				// refresh) reopens the modal.
				const newParams = new URLSearchParams(searchParams);
				newParams.delete("tool");
				setSearchParams(newParams, { replace: true });
				return;
			}

			// Trigger the tool
			const hasResult = toolResults.some(r => {
				// Simple heuristic: if tool name is mentioned in data or something
				// For real production we'd have a tool_slug in ToolResult
				return false; // For now, let it run or add more logic
			});

			if (!hasResult && !loadingTool) {
				handleToolClick(toolFromUrl, toolFromUrl.replace("-", " "));
				// Clear the search params so it doesn't re-run on every refresh
				const newParams = new URLSearchParams(searchParams);
				newParams.delete("tool");
				newParams.delete("platform");
				setSearchParams(newParams, { replace: true });
			}
		}
	}, [projectId, toolFromUrl, toolResults, isLoadingTools]);

	// --- Tool launch handler ---
	const handleToolClick = async (toolSlug: string | null, label: string) => {
		if (!toolSlug || !projectId) return;
		setIsToolPickerOpen(false);

		// Content plan requires platform selection
		if (toolSlug === "content-plan") {
			setPendingToolSlug(toolSlug);
			setSelectedPlatform(null);
			setIsPlatformModalOpen(true);
			return;
		}

		setLoadingTool(toolSlug);
		try {
			await api.runCanvasTool(
				toolSlug,
				projectId,
				(i18n.resolvedLanguage ?? i18n.language ?? "ru") as "en" | "ru",
			);
			toast.success(`${label} started`);
			queryClient.invalidateQueries({ queryKey: ["toolResults", projectId] });
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to start tool",
			);
		} finally {
			setLoadingTool(null);
		}
	};

	// --- Execute tool with platform (for content-plan) ---
	const executeToolWithPlatform = async () => {
		if (!pendingToolSlug || !selectedPlatform || !projectId) return;
		setIsPlatformModalOpen(false);
		setLoadingTool(pendingToolSlug);
		try {
			await api.runCanvasTool(
				pendingToolSlug,
				projectId,
				(i18n.resolvedLanguage ?? i18n.language ?? "ru") as "en" | "ru",
				selectedPlatform,
			);
			toast.success("Content plan started");
			queryClient.invalidateQueries({ queryKey: ["toolResults", projectId] });
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to start content plan",
			);
		} finally {
			setLoadingTool(null);
			setPendingToolSlug(null);
			setSelectedPlatform(null);
		}
	};

	return {
		isToolPickerOpen,
		setIsToolPickerOpen,
		loadingTool,
		isPlatformModalOpen,
		setIsPlatformModalOpen,
		selectedPlatform,
		setSelectedPlatform,
		handleToolClick,
		executeToolWithPlatform,
	};
}
